import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { bookingAPI, paymentAPI } from '../services/api';
import {
  clearPendingStayBooking,
  releasePendingStayBooking,
  getPendingStayId
} from '../utils/pendingStayBooking';
import Footer from '../components/Footer';
import { transformStayBookingForConfirmation } from '../utils/stayBookingConfirmation';
import { normalizeRoomBookingOption, roomBookingOptionLabel } from '../utils/roomBookingOptions';

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [bookingId, setBookingId] = useState('');
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [bookingCreated, setBookingCreated] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [verificationError, setVerificationError] = useState(null);
  const [fetchedBookingData, setFetchedBookingData] = useState(null);
  const bootstrapRef = useRef(false);

  // Function to generate random booking ID
  const generateRandomBookingId = () => {
    const chars = '0123456789abcdef';
    const segments = [
      Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''),
      Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''),
      Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''),
      Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''),
      Array.from({ length: 12 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    ];
    const id = segments.join('-');
    console.log('Generated Booking ID:', id); // Debug log
    return id;
  };

  // Function to generate barcode pattern
  const generateBarcodePattern = (bookingId) => {
    if (!bookingId) return [];

    const pattern = [];
    for (let i = 0; i < 60; i++) {
      const charIndex = i % bookingId.length;
      const char = bookingId[charIndex];
      const charCode = char.charCodeAt(0);
      // Create a deterministic pattern based on the booking ID
      const shouldBeBlack = (charCode + i) % 3 === 0 || (charCode + i) % 5 === 0 || (charCode + i) % 7 === 0;
      pattern.push(shouldBeBlack);
    }
    return pattern;
  };

  // Get data from location state or fetched booking data
  // Prefer fetchedBookingData (transformed) over location.state.bookingData (raw API data)
  const bookingData = fetchedBookingData || location.state?.bookingData;

  // Debug logging
  useEffect(() => {
    console.log('BookingConfirmation - location.state:', location.state);
    console.log('BookingConfirmation - bookingData:', bookingData);
  }, [location.state, bookingData]);

  // Ensure dates are properly formatted strings, not Date objects
  useEffect(() => {
    if (bookingData?.dates) {
      const dates = bookingData.dates;
      if (dates.checkIn instanceof Date) {
        dates.checkIn = dates.checkIn.toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      }
      if (dates.checkOut instanceof Date) {
        dates.checkOut = dates.checkOut.toLocaleDateString('en-US', {
          weekday: 'short',
          day: 'numeric',
          month: 'short'
        });
      }
    }
  }, [bookingData]);

  // Note: We no longer redirect automatically. Instead, we show "booking not successful" 
  // if payment is not confirmed. This ensures users see the status instead of being redirected.

  const handleClose = () => {
    // Navigate back to home or wherever appropriate
    navigate('/');
  };

  // Function to create booking in backend
  const createBooking = async () => {
    if (!isAuthenticated || !user) {
      console.error('User not authenticated, cannot create booking');
      return;
    }

    if (!bookingData) {
      console.error('No booking data available');
      return;
    }

    try {
      setIsCreatingBooking(true);

      // Prepare booking data for API
      const bookingPayload = {
        stayId: bookingData.property?.id || location.state?.propertyId,
        checkIn: bookingData.dates?.checkInDate || new Date(),
        checkOut: bookingData.dates?.checkOutDate || new Date(),
        guestInfo: {
          name: user.name,
          email: user.email,
          mobile: user.mobile
        },
        rooms: bookingData.selectedRooms?.map(room => ({
          roomId: room.id,
          quantity: room.quantity || 1,
          option: normalizeRoomBookingOption(room.selectedOption)
        })) || []
      };

      console.log('Creating booking with payload:', bookingPayload);

      const response = await bookingAPI.createBooking(bookingPayload);
      console.log('Booking created successfully:', response);

      // Update booking ID with the one from backend
      if (response.booking?.bookingId) {
        setBookingId(response.booking.bookingId);
      }

      setBookingCreated(true);

    } catch (error) {
      console.error('Failed to create booking:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const transformBookingData = useCallback(
    (apiBooking) => transformStayBookingForConfirmation(apiBooking, user),
    [user]
  );

  const applyConfirmedBooking = useCallback(
    (apiBooking, confirmationBooking, bookingId, orderId) => {
      const transformed =
        confirmationBooking || transformBookingData(apiBooking);
      if (transformed) {
        setFetchedBookingData(transformed);
        setBookingCreated(true);
      }
      setPaymentStatus('SUCCESS');
      setBookingId(bookingId || apiBooking?.bookingId || '');
      clearPendingStayBooking();
      if (orderId) {
        sessionStorage.removeItem('cashfree_order_id');
      }
    },
    [transformBookingData]
  );

  // Single bootstrap: account view, payment-return handoff, or legacy verify
  useEffect(() => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;

    const fromAccount = location.state?.fromAccount;
    const bookingDataFromAccount = location.state?.bookingData;
    if (fromAccount && bookingDataFromAccount) {
      const transformedData = transformBookingData(bookingDataFromAccount);
      if (transformedData) {
        setFetchedBookingData(transformedData);
        setBookingCreated(true);
        setPaymentStatus('SUCCESS');
        setBookingId(bookingDataFromAccount.bookingId);
      }
      return;
    }

    const orderId =
      searchParams.get('order_id') ||
      searchParams.get('orderId') ||
      location.state?.orderId;

    if (location.state?.paymentVerified) {
      applyConfirmedBooking(
        location.state.bookingFromApi,
        location.state.confirmationBooking,
        location.state.bookingId,
        orderId
      );
      return;
    }

    if (!location.state?.skipPaymentReturn && orderId) {
      navigate(`/stay-payment-return?order_id=${encodeURIComponent(orderId)}`, {
        replace: true
      });
      return;
    }

    if (orderId && location.state?.skipPaymentReturn) {
      verifyPaymentStatus(orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verify payment status from Cashfree
  const verifyPaymentStatus = async (orderId) => {
    try {
      console.log('🚀 Starting payment verification for orderId:', orderId);
      setIsVerifyingPayment(true);
      setVerificationError(null);

      const verificationResult = await paymentAPI.verifyPayment(orderId);
      console.log('📥 Payment verification result:', verificationResult);

      if (verificationResult.paymentStatus === 'SUCCESS') {
        clearPendingStayBooking();
        console.log('✅ Payment verified successfully');
        setPaymentStatus('SUCCESS');
        setBookingId(verificationResult.bookingId);

        // Always fetch booking details after successful payment verification
        if (verificationResult.bookingId) {
          try {
            console.log('📦 Fetching booking details for:', verificationResult.bookingId);
            const bookingResponse = await bookingAPI.getBookingById(verificationResult.bookingId);
            console.log('📥 Booking API response:', bookingResponse);

            if (bookingResponse.booking) {
              // Transform the booking data to match the expected format
              const transformedData = transformBookingData(bookingResponse.booking);
              console.log('✨ Transformed booking data:', transformedData);

              if (transformedData) {
                setFetchedBookingData(transformedData);
                setBookingCreated(true);
                console.log('✅ Booking data set successfully');
              } else {
                console.error('❌ Failed to transform booking data');
              }
            } else {
              console.error('❌ No booking in API response');
            }
          } catch (fetchError) {
            console.error('❌ Failed to fetch booking details:', fetchError);
            console.error('Error details:', {
              message: fetchError.message,
              response: fetchError.response?.data,
              status: fetchError.response?.status
            });
            // Continue anyway - we have booking ID
          }
        } else {
          console.error('❌ No bookingId in verification result');
        }
      } else {
        const bookingId =
          verificationResult.bookingId || orderId.replace(/^order_/, '');
        await releasePendingStayBooking(bookingId);
        clearPendingStayBooking();

        const stayId = verificationResult.stayId || getPendingStayId();
        if (stayId) {
          navigate(`/property/${stayId}/rooms`, {
            replace: true,
            state: {
              paymentIncomplete: true,
              message:
                verificationResult.paymentMessage ||
                'Payment was not completed. You can try booking again.'
            }
          });
          return;
        }

        console.log('❌ Payment not completed:', verificationResult.paymentStatus);
        setPaymentStatus(
          verificationResult.paymentStatus === 'PENDING' ? 'PENDING' : 'FAILED'
        );
        setVerificationError(
          verificationResult.paymentMessage ||
          'Payment was not completed. Please try again.'
        );
      }
    } catch (error) {
      console.error('❌ Payment verification error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setPaymentStatus('FAILED');
      setVerificationError(error.message || 'Failed to verify payment');
    } finally {
      setIsVerifyingPayment(false);
      console.log('🏁 Payment verification completed');
    }
  };

  // Show loading state while verifying payment
  if (isVerifyingPayment) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Verifying payment...</p>
        </div>
      </div>
    );
  }

  // Get orderId for PENDING state handling
  const urlParamsForPending = new URLSearchParams(window.location.search);
  const orderIdForPending =
    urlParamsForPending.get('order_id') ||
    urlParamsForPending.get('orderId') ||
    searchParams.get('order_id') ||
    searchParams.get('orderId') ||
    location.state?.orderId ||
    location.state?.bookingData?.orderId ||
    sessionStorage.getItem('cashfree_order_id');

  // Show payment pending/incomplete state - user came back before completing payment
  if (paymentStatus === 'PENDING' && !isVerifyingPayment) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        {/* Header Section - Coral Red Background */}
        <div className="py-[20px]">
          <div className="flex items-center gap-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
              <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
              <path d="M29.5 15.37V30.37M29.5 45.37V30.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" />
              <circle cx="29.5" cy="30.37" r="3" fill="#FF5E4C" />
            </svg>

            {/* Header Text */}
            <div className="text-[#FCFCFC] leading-none">
              <h1 className="text-[22px] font-medium">Payment In Progress</h1>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-orange-500 text-5xl mb-4">⏳</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Not Completed</h2>
            <p className="text-gray-600 mb-4 px-4">
              It looks like you returned before completing the payment. Your booking is still pending.
            </p>
            {orderIdForPending && (
              <p className="text-sm text-gray-500 mb-6 px-4">
                Order ID: {orderIdForPending}
              </p>
            )}
            <div className="space-y-3">
              <button
                onClick={() => {
                  // Retry verification to check if payment completed
                  if (orderIdForPending) {
                    verifyPaymentStatus(orderIdForPending);
                  }
                }}
                className="w-full bg-[#FF5E4C] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#FF4A3A] transition-colors"
              >
                Check Payment Status
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-gray-200 text-gray-800 py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-gray-300 transition-colors"
              >
                Go Home
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-4 px-4">
              If you completed the payment, click "Check Payment Status" to verify. Otherwise, you can complete the payment from your bookings page.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show payment failed state - booking not successful
  if (paymentStatus === 'FAILED') {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        {/* Header Section - Coral Red Background */}
        <div className="py-[20px]">
          <div className="flex items-center gap-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
              <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
              <path d="M18.75 18.37L40.25 40.37M40.25 18.37L18.75 40.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            {/* Header Text */}
            <div className="text-[#FCFCFC] leading-none">
              <h1 className="text-[22px] font-medium">Booking Not Successful</h1>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6 px-4">
              {verificationError || 'Your payment could not be processed. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check for order_id in URL (from Cashfree redirect) - try multiple variations
  const urlParams = new URLSearchParams(window.location.search);
  const orderId =
    urlParams.get('order_id') ||
    urlParams.get('orderId') ||
    urlParams.get('order-id') ||
    urlParams.get('cf_order_id') ||
    urlParams.get('order') ||
    searchParams.get('order_id') ||
    searchParams.get('orderId') ||
    location.state?.orderId ||
    location.state?.bookingData?.orderId;

  // If payment verification completed successfully but no booking data, show error
  if (paymentStatus === 'SUCCESS' && !bookingData) {
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        {/* Header Section - Coral Red Background */}
        <div className="py-[20px]">
          <div className="flex items-center gap-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
              <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
              <path d="M18.75 18.37L40.25 40.37M40.25 18.37L18.75 40.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            {/* Header Text */}
            <div className="text-[#FCFCFC] leading-none">
              <h1 className="text-[22px] font-medium">Booking Not Successful</h1>
            </div>
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Data Not Available</h2>
            <p className="text-gray-600 mb-6 px-4">
              Payment was successful but booking details could not be retrieved. Please contact support with your booking ID.
            </p>
            {bookingId && (
              <p className="text-sm text-gray-500 mb-4">Booking ID: {bookingId}</p>
            )}
            <button
              onClick={() => navigate('/')}
              className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If no order_id, no booking data, and payment verification hasn't started or completed with failure
  // Show "booking not successful" - this handles cases where user navigates directly or payment wasn't attempted
  if (!orderId && !bookingData && !isVerifyingPayment && paymentStatus !== 'SUCCESS') {
    // Only show this if we're not in the middle of a payment flow
    // If there's location.state with bookingData but paymentSuccess is false, that's a failed payment
    if (!location.state?.bookingData || location.state?.bookingData?.paymentSuccess === false) {
      return (
        <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
          {/* Header Section - Coral Red Background */}
          <div className="py-[20px]">
            <div className="flex items-center gap-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
                <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
                <path d="M18.75 18.37L40.25 40.37M40.25 18.37L18.75 40.37" stroke="#FF5E4C" strokeWidth="4.31825" strokeLinecap="round" strokeLinejoin="round" />
              </svg>

              {/* Header Text */}
              <div className="text-[#FCFCFC] leading-none">
                <h1 className="text-[22px] font-medium">Booking Not Successful</h1>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
            <div className="text-center py-8">
              <div className="text-red-500 text-5xl mb-4">✕</div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Not Confirmed</h2>
              <p className="text-gray-600 mb-6 px-4">
                Your booking could not be confirmed. Please try again or contact support if the issue persists.
              </p>
              <button
                onClick={() => navigate('/')}
                className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // Only show booking data if payment is confirmed (SUCCESS) and booking data exists
  // This is the main success case - payment verified and booking data available
  if (paymentStatus !== 'SUCCESS' || !bookingData) {
    // If we have an order_id but verification hasn't completed yet, wait (loading state already shown)
    // Otherwise, return null to prevent rendering booking data without confirmation
    return null;
  }

  // Ensure bookingData has the required structure (property object)
  // If coming from Account page, the data should be transformed, but add safety check
  if (!bookingData.property) {
    console.error('Booking data missing property object:', bookingData);
    // If we have fetchedBookingData but it's not set yet, wait for it
    if (location.state?.fromAccount && location.state?.bookingData && !fetchedBookingData) {
      // Still transforming, show loading
      return (
        <div className="min-h-screen bg-[#FF5E4C] flex items-center justify-center">
          <div className="text-white text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Loading booking details...</p>
          </div>
        </div>
      );
    }
    // Otherwise, show error
    return (
      <div className="min-h-screen bg-[#FF5E4C] p-[20px]">
        <div className="bg-white rounded-[32px] p-[16px] shadow-sm">
          <div className="text-center py-8">
            <div className="text-red-500 text-5xl mb-4">✕</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Booking Data Error</h2>
            <p className="text-gray-600 mb-6 px-4">
              Unable to load booking details. Please try again or contact support.
            </p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Helper functions for dynamic data
  const getTotalGuests = () => {
    // Use actual guest data if available, otherwise fall back to room sleeps
    if (bookingData?.guestData) {
      return bookingData.guestData.adults + bookingData.guestData.children;
    }
    if (bookingData.selectedRooms) {
      return bookingData.selectedRooms.reduce((total, room) => {
        const sleeps = typeof room.sleeps === 'number' ? room.sleeps :
          typeof room.sleeps === 'string' && !isNaN(parseInt(room.sleeps)) ? parseInt(room.sleeps) : 1;
        return total + (sleeps * (room.quantity || 1));
      }, 0);
    }
    return bookingData.room?.guests || 1;
  };

  const getTotalRooms = () => {
    if (bookingData.selectedRooms) {
      return bookingData.selectedRooms.reduce((total, room) => total + (room.quantity || 1), 0);
    }
    return 1;
  };

  const getRoomTypesText = () => {
    if (bookingData.selectedRooms && bookingData.selectedRooms.length > 0) {
      const uniqueTypes = [...new Set(bookingData.selectedRooms.map(room => room.name || room.bedType))];
      return uniqueTypes.join(', ');
    }
    return bookingData.room?.bedType || 'Standard Room';
  };

  const ROOM_PREVIEW_FALLBACK =
    'https://images.unsplash.com/photo-1566665797739-1674de7a421a?w=400&h=300&fit=crop';

  const getFirstRoomImage = () => {
    if (bookingData.selectedRooms?.length > 0) {
      const r = bookingData.selectedRooms[0];
      const fromRoom = r.image || r.images?.[0];
      if (fromRoom) return fromRoom;
    }
    const fromLegacyRoom =
      bookingData.room?.image || bookingData.room?.images?.[0];
    if (fromLegacyRoom) return fromLegacyRoom;
    return (
      bookingData.property?.image ||
      bookingData.property?.images?.[0] ||
      ROOM_PREVIEW_FALLBACK
    );
  };

  const getDurationLabel = () => {
    if (bookingData.dates?.duration) return bookingData.dates.duration;
    const ci = bookingData.dates?.checkInDate;
    const co = bookingData.dates?.checkOutDate;
    if (!ci || !co) return '1 Night';
    const n = Math.max(
      1,
      Math.ceil((new Date(co) - new Date(ci)) / (1000 * 60 * 60 * 24))
    );
    return `${n} Night${n !== 1 ? 's' : ''}`;
  };

  // Get coordinates from property
  const getCoordinates = () => {
    const lat = bookingData.property?.latitude;
    const lng = bookingData.property?.longitude;

    // Return null if coordinates are not available or invalid
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) {
      return null;
    }

    return { latitude: parseFloat(lat), longitude: parseFloat(lng) };
  };

  // Get Google Maps URL
  const getGoogleMapsUrl = () => {
    const coords = getCoordinates();
    if (coords) {
      return `https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`;
    }
    // Fallback to location search if no coordinates
    const location = bookingData.property?.location || bookingData.property?.address || '';
    if (location) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`;
    }
    return null;
  };

  // Handle map click
  const handleMapClick = () => {
    const mapsUrl = getGoogleMapsUrl();
    if (mapsUrl) {
      window.open(mapsUrl, '_blank', 'noopener,noreferrer');
    }
  };


  return (
    <div>
    <div className="min-h-screen md:min-h-fit bg-[#FF5E4C] p-[20px] md:px-[120px]">
      {/* Header Section - Coral Red Background */}
      <div className="py-[20px]">
        <div className="flex items-center gap-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="59" height="60" viewBox="0 0 59 60" fill="none">
            <circle cx="29.5" cy="30.3701" r="29.5" fill="white" />
            <path d="M18.75 30.37L25.9162 37.5362L40.2485 23.2039" stroke="#FF5E4C" stroke-width="4.31825" stroke-linecap="round" stroke-linejoin="round" />
          </svg>

          {/* Header Text */}
          <div className="text-[#FCFCFC] leading-none">
            <p className="text-[16px] font-medium mb-[2px]">
              Hi {bookingData.user?.firstName || 'Guest'} {bookingData.user?.lastName || ''},
            </p>
            <h1 className="text-[22px] font-medium">Congrats on your booking</h1>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white w-full rounded-[32px] p-[16px] md:p-[64px] shadow-sm overflow-hidden flex flex-col md:flex-row">
        <div className="w-full md:w-[40%] md:mr-[64px] ">
          {/* Map Section - Clickable to open Google Maps */}
          <div className="relative rounded-[14px] bg-gray-100 h-[125px] w-full overflow-hidden">
            <button
              onClick={handleMapClick}
              className="w-full h-full cursor-pointer hover:opacity-90 transition-opacity duration-200 group relative"
              title={getCoordinates() ? 'Click to open in Google Maps' : 'Click to search location in Google Maps'}
              aria-label="Open location in Google Maps"
            >
              {/* Map Background - Static Map Image (optional, requires API key) or placeholder */}
              <div className="w-full h-full relative">
                {getCoordinates() ? (
                  // Map-like background when coordinates are available
                  // Note: To show actual Google Maps embed, add your Google Maps API key:
                  // <iframe src={`https://www.google.com/maps/embed/v1/place?key=YOUR_API_KEY&q=${lat},${lng}&zoom=15`} />
                  <div className="w-full h-full bg-gradient-to-br from-blue-50 via-blue-100 to-gray-100 relative">
                    {/* Grid pattern to simulate map */}
                    <div className="absolute inset-0 opacity-20" style={{
                      backgroundImage: `
                        linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
                      `,
                      backgroundSize: '20px 20px'
                    }}></div>
                  </div>
                ) : (
                  // Placeholder background when no coordinates
                  <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300"></div>
                )}

                {/* Location Pin */}
                <div className="absolute bg-[#FFD2CD20] rounded-full p-[15px] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none group-hover:bg-[#FFD2CD40] transition-all duration-200 z-20">
                  <div className='bg-[#ff5e4c] rounded-full p-[7px]'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                      <path d="M15.4648 7.20762C14.6773 3.74262 11.6548 2.18262 8.99983 2.18262C8.99983 2.18262 8.99983 2.18262 8.99233 2.18262C6.34483 2.18262 3.31483 3.73512 2.52733 7.20012C1.64983 11.0701 4.01983 14.3476 6.16483 16.4101C6.95983 17.1751 7.97983 17.5576 8.99983 17.5576C10.0198 17.5576 11.0398 17.1751 11.8273 16.4101C13.9723 14.3476 16.3423 11.0776 15.4648 7.20762ZM8.99983 10.9651C7.69483 10.9651 6.63733 9.90762 6.63733 8.60262C6.63733 7.29762 7.69483 6.24012 8.99983 6.24012C10.3048 6.24012 11.3623 7.29762 11.3623 8.60262C11.3623 9.90762 10.3048 10.9651 8.99983 10.9651Z" fill="#FEFEFE" />
                    </svg>
                  </div>
                </div>
                <img src="https://bookforpaws-assets.s3.ap-south-1.amazonaws.com/features/map.png" alt="Property" className="w-full h-full absolute z-10 top-0 left-0 object-cover" />

                {/* Overlay hint text on hover */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-[10px] px-2 py-1 rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                  Tap to open in Google Maps
                </div>
              </div>
            </button>

            {/* Hotel Room Preview Image */}
            <div className="absolute bottom-4 left-4 z-20 pointer-events-none">
              <img
                src={getFirstRoomImage()}
                alt="Room Preview"
                className="w-[50px] h-[50px] rounded-[9px] object-cover shadow-md"
              />
            </div>
          </div>

          {/* Hotel Information */}
          <div className="pt-[20px]">
            <h2 className="text-[16px] font-medium text-black leading-none">
              {bookingData.property?.name || 'Property'}
            </h2>
            <p className="text-[14px] text-[#78736A] font-medium leading-tight mb-[8px]">
              {getTotalRooms()} x {getRoomTypesText()}
            </p>

            {/* Selected Rooms Details */}
            {/* {bookingData.selectedRooms && bookingData.selectedRooms.length > 0 && (
              <div className="mb-4 space-y-2">
                {bookingData.selectedRooms.map((room, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 text-sm">{room.name}</h4>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                        {room.quantity || 1} × {roomBookingOptionLabel(room.selectedOption)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span>Sleeps: {room.sleeps || 'N/A'} • {room.bedType || 'N/A'}</span>
                      <span className="font-medium">₹{((room.price || 0) * (room.quantity || 1)).toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            )} */}

            {/* Location */}
            <div className="flex items-center gap-[4px] ">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
                <g clip-path="url(#clip0_2904_5643)">
                  <path d="M5.25 7.28687C5.25 7.75099 5.43437 8.19611 5.76256 8.5243C6.09075 8.85249 6.53587 9.03687 7 9.03687C7.46413 9.03687 7.90925 8.85249 8.23744 8.5243C8.56563 8.19611 8.75 7.75099 8.75 7.28687C8.75 6.82274 8.56563 6.37762 8.23744 6.04943C7.90925 5.72124 7.46413 5.53687 7 5.53687C6.53587 5.53687 6.09075 5.72124 5.76256 6.04943C5.43437 6.37762 5.25 6.82274 5.25 7.28687Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M10.3006 10.5868L7.82549 13.0619C7.60673 13.2804 7.31016 13.4032 7.00094 13.4032C6.69173 13.4032 6.39516 13.2804 6.1764 13.0619L3.70074 10.5868C3.04812 9.93412 2.60368 9.1026 2.42364 8.19736C2.24359 7.29212 2.33602 6.35382 2.68924 5.50112C3.04245 4.64841 3.64059 3.91959 4.40802 3.40682C5.17544 2.89405 6.07768 2.62036 7.00065 2.62036C7.92362 2.62036 8.82586 2.89405 9.59329 3.40682C10.3607 3.91959 10.9589 4.64841 11.3121 5.50112C11.6653 6.35382 11.7577 7.29212 11.5777 8.19736C11.3976 9.1026 10.9532 9.93412 10.3006 10.5868Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                </g>
                <defs>
                  <clipPath id="clip0_2904_5643">
                    <rect width="14" height="14" fill="white" transform="translate(0 0.870117)" />
                  </clipPath>
                </defs>
              </svg>
              <span className="text-[13px] text-[#78736A] font-normal leading-tight truncate">{bookingData.property?.location || 'Location not available'}</span>
            </div>
          </div>
        </div>
        <div className="w-full md:w-[40%]">
          {/* Divider */}
          <div className='flex my-[20px] md:mt-0 items-center justify-center'>
            <svg width="100%" height="1px">
              <line
                x1="0" y1="1" x2="100%" y2="1"
                stroke="#f0f0f0"
                strokeWidth="2"
                strokeDasharray="10 4"
              />
            </svg>
          </div>

          {/* Check-in/Check-out Details */}
          <div className="flex items-center justify-between">
            {/* Check-in */}
            <div className="flex flex-col gap-[6px] items-start ">
              <div className="flex items-center space-x-[4px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <g clip-path="url(#clip0_2846_6297)">
                    <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33203 1.75V4.08333" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.66797 1.75V4.08333" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M2.33203 6.41675H11.6654" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2846_6297">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-[12px] text-[#999999]">Check in</span>
              </div>
              <div className="">
                <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.dates.checkIn}</div>
                <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.dates.checkInTime}</div>
              </div>
            </div>

            {/* Duration */}
            <div className="text-center">
              <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">
                {getDurationLabel()}
              </span>
            </div>

            {/* Check-out */}
            <div className="flex flex-col gap-[6px] items-end">
              <div className="flex items-center space-x-[4px]">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <g clip-path="url(#clip0_2846_6297)">
                    <path d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M9.33203 1.75V4.08333" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.66797 1.75V4.08333" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M2.33203 6.41675H11.6654" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M4.66797 8.75H5.83464V9.91667H4.66797V8.75Z" stroke="black" stroke-width="0.583333" stroke-linecap="round" stroke-linejoin="round" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2846_6297">
                      <rect width="14" height="14" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                <span className="text-[12px] text-[#999999]">Check out</span>
              </div>
              <div className="text-end">
                <div className="font-normal text-[18px] leading-none mb-[6px]">{bookingData.dates.checkOut}</div>
                <div className="text-[14px] font-medium leading-none text-[#636363]">{bookingData.dates.checkOutTime}</div>
              </div>
            </div>


          </div>

          {/* Divider */}
          <div className='flex my-[20px] items-center justify-center'>
            <svg width="100%" height="1px">
              <line
                x1="0" y1="1" x2="100%" y2="1"
                stroke="#f0f0f0"
                strokeWidth="2"
                strokeDasharray="10 4"
              />
            </svg>
          </div>

          {/* Guest, Room Type, and Phone Details */}
          <div className="">
            {/* Guest */}
            <div className="flex items-center py-[8px] justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M10.0007 10.8702C12.3018 10.8702 14.1673 9.00472 14.1673 6.70353C14.1673 4.40235 12.3018 2.53687 10.0007 2.53687C7.69946 2.53687 5.83398 4.40235 5.83398 6.70353C5.83398 9.00472 7.69946 10.8702 10.0007 10.8702Z" stroke="#78828A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M17.1585 19.2035C17.1585 15.9785 13.9501 13.3701 10.0001 13.3701C6.05013 13.3701 2.8418 15.9785 2.8418 19.2035" stroke="#78828A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span className="text-[14px] font-medium text-black">Guest</span>
              </div>
              <span className="text-[14px] text-black font-medium">{getTotalGuests()} Guests ({getTotalRooms()} {getTotalRooms() === 1 ? 'Room' : 'Rooms'})</span>
            </div>

            {/* Room Type */}
            <div className="flex items-start py-[8px]  justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M1.66602 19.2034H18.3327" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M14.1667 2.53687H5.83333C3.33333 2.53687 2.5 4.02853 2.5 5.8702V19.2035H17.5V5.8702C17.5 4.02853 16.6667 2.53687 14.1667 2.53687Z" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5.83398 14.6201H8.33398" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M11.666 14.6201H14.166" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5.83398 10.8701H8.33398" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M11.666 10.8701H14.166" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M5.83398 7.12012H8.33398" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M11.666 7.12012H14.166" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span className="text-[14px] text-nowrap leading-none font-medium text-black">Room type</span>
              </div>
              <span className="text-[14px] leading-none text-wrap text-right text-black font-medium">{getRoomTypesText()}</span>
            </div>

            {/* Phone */}
            <div className="flex items-center py-[8px] justify-between">
              <div className="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                  <path d="M18.3077 16.1452C18.3077 16.4452 18.241 16.7535 18.0993 17.0535C17.9577 17.3535 17.7744 17.6369 17.5327 17.9035C17.1244 18.3535 16.6743 18.6785 16.166 18.8869C15.666 19.0952 15.1244 19.2035 14.541 19.2035C13.691 19.2035 12.7827 19.0035 11.8243 18.5952C10.866 18.1869 9.90768 17.6369 8.95768 16.9452C7.99935 16.2452 7.09102 15.4702 6.22435 14.6119C5.36602 13.7452 4.59102 12.8369 3.89935 11.8869C3.21602 10.9369 2.66602 9.98687 2.26602 9.0452C1.86602 8.0952 1.66602 7.18687 1.66602 6.3202C1.66602 5.75353 1.76602 5.21187 1.96602 4.71187C2.16602 4.20353 2.48268 3.73687 2.92435 3.3202C3.45768 2.7952 4.04102 2.53687 4.65768 2.53687C4.89102 2.53687 5.12435 2.58687 5.33268 2.68687C5.54935 2.78687 5.74102 2.93687 5.89102 3.15353L7.82435 5.87853C7.97435 6.08687 8.08268 6.27853 8.15768 6.46187C8.23268 6.63687 8.27435 6.81187 8.27435 6.9702C8.27435 7.1702 8.21602 7.3702 8.09935 7.56187C7.99102 7.75353 7.83268 7.95353 7.63268 8.15353L6.99935 8.81187C6.90768 8.90353 6.86602 9.01187 6.86602 9.1452C6.86602 9.21187 6.87435 9.2702 6.89102 9.33687C6.91602 9.40353 6.94102 9.45353 6.95768 9.50353C7.10768 9.77853 7.36602 10.1369 7.73268 10.5702C8.10768 11.0035 8.50768 11.4452 8.94102 11.8869C9.39102 12.3285 9.82435 12.7369 10.266 13.1119C10.6993 13.4785 11.0577 13.7285 11.341 13.8785C11.3827 13.8952 11.4327 13.9202 11.491 13.9452C11.5577 13.9702 11.6243 13.9785 11.6993 13.9785C11.841 13.9785 11.9493 13.9285 12.041 13.8369L12.6743 13.2119C12.8827 13.0035 13.0827 12.8452 13.2743 12.7452C13.466 12.6285 13.6577 12.5702 13.866 12.5702C14.0243 12.5702 14.191 12.6035 14.3743 12.6785C14.5577 12.7535 14.7494 12.8619 14.9577 13.0035L17.716 14.9619C17.9327 15.1119 18.0827 15.2869 18.1743 15.4952C18.2577 15.7035 18.3077 15.9119 18.3077 16.1452Z" stroke="#78828A" stroke-width="1.5" stroke-miterlimit="10" />
                </svg>
                <span className="text-[14px] font-medium text-black">Phone</span>
              </div>
              <span className="text-[14px] text-black font-medium">
                {bookingData.user?.mobile || 'Not provided'}
              </span>
            </div>
          </div>
        </div>
        {/* Divider */}
        <div className='flex my-[20px] items-center justify-center md:hidden'>
          <svg width="100%" height="1px">
            <line
              x1="0" y1="1" x2="100%" y2="1"
              stroke="#f0f0f0"
              strokeWidth="2"
              strokeDasharray="10 4"
            />
          </svg>
        </div>
        {/* Divider */}
        <div className='hidden md:flex items-center justify-center mx-[64px]'>
          <svg width="1px" height="100%">
            <line
              x1="1" y1="0" x2="1" y2="100%"
              stroke="#f0f0f0"
              strokeWidth="2"
              strokeDasharray="10 4"
            />
          </svg>
        </div>

        {/* Pricing Summary */}
        {/* {bookingData.pricing && (
              <div className="mb-6">
                <h3 className="text-[16px] font-medium text-gray-800 mb-3">Pricing Summary</h3>
                <div className="space-y-2">
                  {bookingData.selectedRooms && bookingData.selectedRooms.length > 0 ? (
                    // Show breakdown for each selected room
                    <>
                      {bookingData.selectedRooms.map((room, index) => (
                        <div key={index} className="space-y-1">
                          <div className="flex justify-between items-center">
                            <span className="text-[14px] text-gray-600">
                              {room.quantity || 1} × {room.name}
                            </span>
                            <span className="text-[14px] font-medium text-gray-900">
                              ₹{((room.price || 0) * (room.quantity || 1)).toLocaleString()}
                            </span>
                          </div>
                          {room.taxes > 0 && (
                            <div className="flex justify-between items-center ml-4">
                              <span className="text-[14px] text-gray-600">
                                Included Taxes & Fees (×{room.quantity || 1})
                              </span>
                              <span className="text-[14px] font-medium text-gray-900">
                                ₹{((room.taxes || 0) * (room.quantity || 1)).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="pt-2 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                          <span className="text-[14px] text-gray-600">Subtotal:</span>
                          <span className="text-[14px] font-medium text-gray-900">
                            ₹{(() => {
                              const subtotal = bookingData.selectedRooms.reduce((total, room) => {
                                return total + ((room.price || 0) * (room.quantity || 1));
                              }, 0);
                              return subtotal.toLocaleString();
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-[14px] text-gray-600">Included Taxes:</span>
                          <span className="text-[14px] font-medium text-gray-900">
                            ₹{(() => {
                              const totalTaxes = bookingData.selectedRooms.reduce((total, room) => {
                                return total + ((room.taxes || 0) * (room.quantity || 1));
                              }, 0);
                              return totalTaxes.toLocaleString();
                            })()}
                          </span>
                        </div>
                      </div>
                    </>
                  ) : (
                    // Fallback to old pricing display
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-[14px] text-gray-600">Room Charges:</span>
                        <span className="text-[14px] font-medium text-gray-900">
                          ₹{bookingData.pricing.roomPrice?.toLocaleString() || '0'}
                        </span>
                      </div>
                      {bookingData.pricing.taxes > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-[14px] text-gray-600">Included Taxes & Fees:</span>
                          <span className="text-[14px] font-medium text-gray-900">
                            ₹{bookingData.pricing.taxes.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {bookingData.pricing.discount > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] text-gray-600">Discount:</span>
                      <span className="text-[14px] font-medium text-green-600">
                        -₹{bookingData.pricing.discount.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                    <span className="text-[16px] font-bold text-gray-900">Total Amount:</span>
                    <span className="text-[18px] font-bold text-gray-900">
                      ₹{(() => {
                        if (bookingData.selectedRooms && bookingData.selectedRooms.length > 0) {
                          // Calculate total from selected rooms with quantities
                          return bookingData.selectedRooms.reduce((total, room) => {
                            const roomTotal = (room.price || 0) * (room.quantity || 1);
                            return total + roomTotal;
                          }, 0).toLocaleString();
                        } else {
                          // Fallback to pricing object
                          return (bookingData.pricing?.total || 0).toLocaleString();
                        }
                      })()}
                    </span>
                  </div>
                </div>
              </div>
            )} */}

        {/* Barcode Section */}
        <div className="text-center flex flex-col w-full md:w-[10%] items-center justify-center rotate-0 md:rotate-[-90deg] ">


          {/* Barcode */}
          <div className="flex w-full md:w-fit justify-center mb-[10px] md:mb-0">
            <div className="bg-white p-0 max-w-[80%] md:max-w-fit rounded-lg ">
              {bookingId ? (
                <div className="flex space-x-[2px]">
                  {generateBarcodePattern(bookingId).map((isBlack, i) => (
                    <div
                      key={i}
                      className={`w-[2px] h-16 ${isBlack ? 'bg-black' : 'bg-white'
                        }`}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-[#78828A] text-[12px] font-medium">Loading barcode...</div>
              )}
            </div>
          </div>

          {/* Booking ID */}
          <p className="text-[12px] text-[#78828A] leading-[0.5] font-medium">{bookingId}</p>
        </div>


      </div>

      {/* Footer Section - Close Button */}
      <div className="pt-[6px] md:hidden">
        <button
          onClick={handleClose}
          className="w-full bg-[#131313] text-white py-4 rounded-[14px] h-[56px] font-bold text-lg hover:bg-[#131313] transition-colors"
        >
          Close
        </button>
      </div>
      </div>
      {/* Footer - Desktop Only */}
      <div className="hidden md:block">
        <Footer />
      </div>

      {/* Debug Section - Development Only */}

    </div>
  );
};

export default BookingConfirmation;
